import { useCallback } from "react";

import toast from "react-hot-toast";
import { Link } from "react-router-dom";

import { Button, Card, Header, Icon, Message } from "simplydo/react/ui";

import { useAppSelector } from "store";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";

import type { InviteCardProps } from "./types";

const RequestJoinGroupInviteCard = ({
  invitation,
  acceptInvite,
  rejectInvite,
  removeInvite,
  cardView = true,
}: InviteCardProps) => {
  const { inviterUser, group, createdByCurrentUser } = invitation;
  const user = useAppSelector((state) => state.user);
  const accept = useCallback(() => {
    util
      .confirm(
        "Allow group join",
        `Are you sure you want to allow ${inviterUser?.profile?.fullName} to join "${group?.name}"?`,
      )
      .then(() => {
        acceptInvite(
          invitation,
          () => {
            toast.success("Accepted request");
          },
          () => toast.error("Error while accepting request"),
        );
      })
      .catch(() => {});
  }, [acceptInvite, invitation, group?.name, inviterUser?.profile?.fullName]);

  const reject = useCallback(() => {
    util
      .confirm(
        "Reject group join",
        `Are you sure you want to reject ${inviterUser?.profile?.fullName} from joining "${group?.name || "Deleted group"}"?`,
      )
      .then(() => {
        rejectInvite(
          invitation,
          () => {
            toast("Request rejected");
          },
          () => toast.error("Error rejecting request"),
        );
      })
      .catch(() => {});
  }, [rejectInvite, invitation, group?.name, inviterUser?.profile?.fullName]);

  const remove = useCallback(() => {
    util
      .confirm(
        "Remove request",
        `Are you sure you want to remove your request to join "${group?.name || "Deleted group"}"?`,
      )
      .then(() => {
        removeInvite(
          invitation,
          () => {
            toast("Invitation removed");
          },
          () => toast.error("Error removing invite"),
        );
      })
      .catch(() => {});
  }, [removeInvite, invitation, group?.name]);

  const canAccept = invitation.canAccept && !!group;

  return (
    <div
      style={
        cardView ?
          {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }
        : {}
      }
    >
      <div>
        {cardView ?
          <Header
            style={{ marginBottom: "0.3em" }}
            as={Link}
            disabled={!group}
            to={group ? `/groups/${group._id}` : null}
          >
            {group?.name || "Deleted group"}
          </Header>
        : <Header as="h2" icon textAlign="center">
            <Icon name="users" circular />
            <Header.Content>
              Request to join {group?.name || "Deleted group"}
            </Header.Content>
          </Header>
        }
        <Card.Description>
          {createdByCurrentUser ?
            "You have requested to join this group"
          : <UserChip
              compact="very"
              user={inviterUser}
              postText="has requested to join this group"
            />
          }
        </Card.Description>
      </div>
      <Card.Content extra>
        <div
          className="ui"
          style={
            cardView ?
              {}
            : { marginTop: 20, display: "flex", justifyContent: "flex-end" }
          }
        >
          {invitation.acceptedAt && group && (
            <Button basic color="green" as={Link} to={`/groups/${group?._id}`}>
              Go to group
            </Button>
          )}
          {!invitation.acceptedAt && (
            <>
              {!group && (
                <Message error style={{ width: "100%" }}>
                  This request to join is no longer valid because the group has
                  been deleted.
                </Message>
              )}
              {user && invitation.canRemove ?
                <Button
                  basic
                  color={createdByCurrentUser ? undefined : "red"}
                  onClick={createdByCurrentUser ? remove : reject}
                >
                  {createdByCurrentUser ? "Remove" : "Reject"}
                </Button>
              : null}
              {user && canAccept ?
                <Button basic color="green" onClick={accept}>
                  Accept
                </Button>
              : null}
            </>
          )}
        </div>
      </Card.Content>
    </div>
  );
};

export default RequestJoinGroupInviteCard;
